/**
 * Разбор аналитических данных карточки: рынок и признаки внимания.
 *
 * Чистые функции без React — их проверяет отдельный тест, а разметка занимается
 * только показом. Здесь же держатся два правила, которые нельзя нарушить в
 * разметке:
 *
 * * **число показывается только вместе с основанием.** `marketNumbers` отдаёт
 *   значения одним пакетом: медиана без размера выборки и даты снимка на экран
 *   не попадает (MVP_PRD §16). Backend это гарантирует схемой, фронт повторяет
 *   проверку, потому что «показать медиану, а выборку не показать» — не ошибка
 *   типов, а ошибка смысла;
 * * **вывод без evidence не создаётся** (MVP_PRD §14). `attentionParams`
 *   возвращает `null`, если в основании не хватает числа, и тогда признак
 *   показывается одним заголовком, без придуманной подробности.
 *
 * Числа форматируются здесь, а не в i18next: форматтер `Intl` в i18n проекта не
 * подключён, поэтому в шаблоны уходят готовые строки — тот же приём, что и в
 * остальных подписях каталога.
 */

import type { AttentionFlagData, MarketComparisonData, VehiclePrice } from './types';

/**
 * Имя поля происхождения, под которым backend хранит рыночный расчёт
 * (`benchmarks.repository.MARKET_EVALUATION_PROVENANCE_FIELD`). Модалка
 * происхождения по нему выделяет запись, относящуюся к блоку сравнения.
 */
export const MARKET_EVALUATION_FIELD = 'market_evaluation';

/**
 * Половина ширины шкалы отклонения, в процентах. Медиана стоит в центре, ±25 %
 * упираются в края: дальше двигать маркер бессмысленно, разница уже видна.
 */
export const MARKET_SCALE_HALF_RANGE_PERCENT = 25;

/** Отклонение цены от медианы. Направление называется словом, а не только цветом. */
export type MarketDeltaDirection = 'below' | 'above' | 'equal';

/** Числа сравнения — только целым набором. */
export interface MarketNumbers {
  median: VehiclePrice;
  deltaCents: number;
  deltaPercent: number;
  sampleSize: number;
  /** Дата снимка выборки, `YYYY-MM-DD`. */
  snapshotDate: string;
  direction: MarketDeltaDirection;
}

/**
 * Числа сравнения, если они есть все сразу.
 *
 * При статусах `unavailable` (снимок старше 90 дней) и `insufficient_data`
 * backend числовых полей не отдаёт вовсе, поэтому проверка статуса здесь не
 * дублируется: достаточно того, что в наборе нет пропусков.
 */
export function marketNumbers(market: MarketComparisonData): MarketNumbers | null {
  const { median_price, delta_cents, delta_percent, sample_size, snapshot_date } = market;
  if (
    median_price === null ||
    delta_cents === null ||
    delta_percent === null ||
    sample_size === null ||
    snapshot_date === null
  ) {
    return null;
  }

  return {
    median: median_price,
    deltaCents: delta_cents,
    deltaPercent: delta_percent,
    sampleSize: sample_size,
    snapshotDate: snapshot_date,
    direction: delta_cents === 0 ? 'equal' : delta_cents < 0 ? 'below' : 'above',
  };
}

/** Отклонение в процентах со знаком: `−5,2 %`. Знак ставит `Intl`, а не шаблон. */
export function formatSignedPercent(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    signDisplay: 'exceptZero',
    maximumFractionDigits: 1,
  }).format(value / 100);
}

function money(cents: number, currency: string, locale: string, signed: boolean): string {
  const options: Intl.NumberFormatOptions = {
    maximumFractionDigits: 0,
    ...(signed ? { signDisplay: 'exceptZero' as const } : {}),
  };
  try {
    return new Intl.NumberFormat(locale, { ...options, style: 'currency', currency }).format(
      cents / 100,
    );
  } catch {
    // Валюта вне списка Intl — показываем число без символа, а не роняем блок.
    return new Intl.NumberFormat(locale, options).format(cents / 100);
  }
}

/** Отклонение в деньгах со знаком: `−1 600 €`. Центы делятся только для показа. */
export function formatSignedMoney(cents: number, currency: string, locale: string): string {
  return money(cents, currency, locale, true);
}

/** Деньги без знака: `24 000 €`. */
export function formatMoney(cents: number, currency: string, locale: string): string {
  return money(cents, currency, locale, false);
}

/** Проценты без знака: `20,8 %`. Для формулировок, где направление уже в тексте. */
export function formatPercent(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);
}

/**
 * Положение маркера цены на шкале, 0–100 (медиана — 50).
 *
 * Значения за пределами диапазона прижимаются к краю: шкала показывает
 * направление и порядок отклонения, а не является графиком распределения —
 * распределения в данных нет, есть только медиана и размер выборки.
 */
export function marketScalePosition(deltaPercent: number): number {
  const clamped = Math.max(
    -MARKET_SCALE_HALF_RANGE_PERCENT,
    Math.min(MARKET_SCALE_HALF_RANGE_PERCENT, deltaPercent),
  );
  return 50 + (clamped / MARKET_SCALE_HALF_RANGE_PERCENT) * 50;
}

// --- Признаки внимания -----------------------------------------------------------------

/** Порядок вывода задаёт backend (`attention.domain.DISPLAY_ORDER`), фронт его сохраняет. */
export interface AttentionParamsOptions {
  locale: string;
  currency: string;
}

function evidenceNumber(evidence: Record<string, unknown>, key: string): number | null {
  const value = evidence[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function evidenceStrings(evidence: Record<string, unknown>, key: string): string[] {
  const value = evidence[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/**
 * Параметры подстановки для текста признака.
 *
 * `null` означает «в основании не хватает числа»: показывать формулировку с
 * дырой вместо величины нельзя, поэтому разметка в этом случае оставляет только
 * заголовок. Пустой объект — нормальный результат для правил, текст которых
 * чисел не содержит («в объявлении нет сведений об аварийной истории»).
 */
export function attentionParams(
  flag: AttentionFlagData,
  { locale, currency }: AttentionParamsOptions,
): Record<string, string> | null {
  const { evidence } = flag;
  const number = (key: string) => evidenceNumber(evidence, key);

  switch (flag.code) {
    case 'PRICE_SIGNIFICANTLY_BELOW_MEDIAN': {
      const percent = number('delta_percent');
      const median = number('median_price_cents');
      const sample = number('sample_size');
      if (percent === null || median === null || sample === null) return null;
      // Ключ `count` здесь не используется: у i18next это управляющая опция
      // выбора формы множественного числа, а сюда уходит уже готовая строка.
      return {
        percent: formatPercent(Math.abs(percent), locale),
        median: formatMoney(median, currency, locale),
        sample: new Intl.NumberFormat(locale).format(sample),
      };
    }
    case 'STALE_LISTING': {
      const days = number('age_days');
      const threshold = number('threshold_days');
      if (days === null || threshold === null) return null;
      return { days: String(days), threshold: String(threshold) };
    }
    case 'FEW_PHOTOS': {
      const photos = number('photo_count');
      const recommended = number('recommended_count');
      if (photos === null || recommended === null) return null;
      return { photos: String(photos), recommended: String(recommended) };
    }
    case 'DESCRIPTION_TOO_SHORT': {
      const length = number('length');
      const min = number('min_length');
      if (length === null || min === null) return null;
      return { length: String(length), min: String(min) };
    }
    case 'INCOMPLETE_DATA': {
      const score = number('completeness_score');
      const threshold = number('threshold');
      if (score === null || threshold === null) return null;
      return { score: formatPercent(score, locale), threshold: String(threshold) };
    }
    // Остальные правила говорят об отсутствии сведений: числа в тексте не нужны.
    default:
      return {};
  }
}

/**
 * Причины слабого рыночного сравнения (`WEAK_MARKET_COMPARISON`).
 *
 * Backend перечисляет их кодами (`broad_match_level`, `small_sample`,
 * `stale_snapshot`); тексты — в i18n. Неизвестный код отбрасывается: показать
 * сырую строку правила пользователю нельзя.
 */
export const WEAK_MARKET_REASONS = ['broad_match_level', 'small_sample', 'stale_snapshot'] as const;

export type WeakMarketReason = (typeof WEAK_MARKET_REASONS)[number];

export function weakMarketReasons(flag: AttentionFlagData): WeakMarketReason[] {
  if (flag.code !== 'WEAK_MARKET_COMPARISON') return [];
  const known = new Set<string>(WEAK_MARKET_REASONS);
  return evidenceStrings(flag.evidence, 'reasons').filter((reason): reason is WeakMarketReason =>
    known.has(reason),
  );
}
